import { motion, Variants } from "motion/react";
import { useEffect, useState } from "react";
import { Card, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { SectionDefaultProps } from "#app/types/type.ts";
import { z } from "zod";
import { zFeatureListSection } from "#app/types/schema.ts";
import { stegaClean } from "@sanity/client/stega";
import { cn } from "#app/utils/misc.tsx";
import { Heading } from "./text";

const MotionCard = motion.create(Card);

const cardVariants: Variants = {
	offscreen: {
	  x: -300,
	  opacity: 0
	},
	onscreen: {
	  x: 0,
	  opacity: 1,
	  transition: {
		type: "spring",
		bounce: 0.4,
		duration: 0.8,
	  }
	}
};

type FeatureListSectionProps = SectionDefaultProps & {
    data: z.infer<typeof zFeatureListSection>;
}

const colorSchemes = {
	default: "bg-background text-foreground",
	primary: "bg-primary text-primary-foreground",
	secondary: "bg-secondary text-secondary-foreground",
	muted: "bg-muted text-foreground",
	accent: "bg-accent text-accent-foreground",
}

const descriptionColors = {
	default: "text-muted-foreground",
	primary: "text-primary-foreground/80",
	secondary: "text-secondary-foreground/80",
	muted: "text-muted-foreground",
	accent: "text-accent-foreground/80",
}

export default function FeatureListSection({ data, encodeDataAttribute }: FeatureListSectionProps) {
    const featureListData = stegaClean(data);
	const { heading, headingLevel, features, backgroundStyle, textAlign } = featureListData;
	const [isMobile, setIsMobile] = useState(true);
	useEffect(() => {
		setIsMobile(window.innerWidth < 1024);
	}, [])

	return (
		<div>
			{heading && (
				<div className={cn(
					"container mb-12 sm:mb-20",
					textAlign === "center" ? "text-center" : "text-left"
				)}>
					<Heading as={headingLevel} className="mx-auto">
						{heading}
					</Heading>
				</div>
			)}
			<div className="container">
				<motion.div
					initial="offscreen"
					whileInView="onscreen"
					viewport={{ once: true, amount: isMobile ? 0.2 : 0.8 }}
					transition={{ staggerChildren: 0.2, staggerDirection: -1 }}
					className="mx-auto grid max-w-2xl auto-rows-fr grid-cols-1 gap-8 lg:mx-0 lg:max-w-none lg:grid-cols-3"
				>
                    {featureListData.features.map((feature, i) => {
                        const classNames = cn(
                            'min-h-[353px] flex flex-col justify-center order-3',
                            colorSchemes[feature.backgroundStyle],
                        )
                        const descriptionClassNames = cn(
                            'text-xl text-center mx-auto pt-4',
                            descriptionColors[feature.backgroundStyle],
                        )
                        return (
                            <MotionCard variants={cardVariants} className={classNames} key={feature._key}>
                                <CardHeader>
                                    <CardTitle className='text-center uppercase text-4xl'>{feature.title}</CardTitle>
                                    <CardDescription className={descriptionClassNames}>
                                        {feature.description}
                                    </CardDescription>
                                </CardHeader>
                            </MotionCard>
                        )
                    })}
				</motion.div>
			</div>
		</div>
	)
}